// src/pages/EmployeeManagement.jsx

import React, { useCallback, useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Space,
  Typography,
  Card,
  message,
  Tooltip,
  Row,
  Col,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  ReloadOutlined,
  IdcardOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  getEmployeesApi,
  createEmployeeApi,
  updateEmployeeApi,
  deleteEmployeeApi,
  getEmployeeUserLinkOptionsApi,
} from "../api/employee.api";
import { useAuth } from "../context/AuthContext";

const { Title, Text } = Typography;
const { Option } = Select;

const toInputDate = (d) => {
  if (!d) return "";
  const s = typeof d === "string" ? d : String(d);
  return s.slice(0, 10);
};

const EmployeeManagement = () => {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("employees.write");

  const [employees, setEmployees] = useState([]);
  const [linkUsers, setLinkUsers] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [tableLoading, setTableLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const fetchLinkUsers = useCallback(async () => {
    try {
      const { data } = await getEmployeeUserLinkOptionsApi();
      setLinkUsers(data.data.users || []);
    } catch {
      setLinkUsers([]);
    }
  }, []);

  const fetchEmployees = useCallback(async (page = 1, limit = 10, q = "") => {
    setTableLoading(true);
    try {
      const { data } = await getEmployeesApi(page, limit, q);
      setEmployees(data.data.employees);
      setPagination((prev) => ({
        ...prev,
        current: data.data.pagination.page,
        total: data.data.pagination.total,
        pageSize: data.data.pagination.limit,
      }));
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to load employees.");
    } finally {
      setTableLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmployees(1, pagination.pageSize, searchText);
  }, [fetchEmployees, pagination.pageSize, searchText]);

  useEffect(() => {
    fetchLinkUsers();
  }, [fetchLinkUsers]);

  const handleCreate = async (values) => {
    setCreateLoading(true);
    try {
      const payload = {
        ...values,
        user_id: values.user_id || null,
        hire_date: values.hire_date || null,
      };
      await createEmployeeApi(payload);
      message.success("Employee created.");
      setCreateOpen(false);
      createForm.resetFields();
      fetchEmployees(pagination.current, pagination.pageSize, searchText);
    } catch (err) {
      const backendErrors = err.response?.data?.errors;
      if (backendErrors?.length) {
        createForm.setFields(
          backendErrors.map((e) => ({ name: e.field, errors: [e.message] })),
        );
      } else {
        message.error(err.response?.data?.message || "Create failed.");
      }
    } finally {
      setCreateLoading(false);
    }
  };

  const openEdit = (row) => {
    setSelected(row);
    editForm.setFieldsValue({
      employee_code: row.employee_code || undefined,
      full_name: row.full_name,
      email: row.email || undefined,
      phone: row.phone || undefined,
      department: row.department || undefined,
      job_title: row.job_title || undefined,
      user_id: row.user_id || undefined,
      hire_date: toInputDate(row.hire_date) || undefined,
      is_active: Boolean(row.is_active),
    });
    setEditOpen(true);
  };

  const handleUpdate = async (values) => {
    if (!selected?.id) return;
    setEditLoading(true);
    try {
      await updateEmployeeApi(selected.id, {
        ...values,
        user_id: values.user_id || null,
        hire_date: values.hire_date || null,
      });
      message.success("Employee updated.");
      setEditOpen(false);
      setSelected(null);
      editForm.resetFields();
      fetchEmployees(pagination.current, pagination.pageSize, searchText);
    } catch (err) {
      const backendErrors = err.response?.data?.errors;
      if (backendErrors?.length) {
        editForm.setFields(
          backendErrors.map((e) => ({ name: e.field, errors: [e.message] })),
        );
      } else {
        message.error(err.response?.data?.message || "Update failed.");
      }
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async (row) => {
    try {
      await deleteEmployeeApi(row.id);
      message.success("Employee deactivated.");
      fetchEmployees(pagination.current, pagination.pageSize, searchText);
    } catch (err) {
      message.error(err.response?.data?.message || "Delete failed.");
    }
  };

  const handleSearch = (value) => {
    const next = value.trim();
    setSearchText(next);
    fetchEmployees(1, pagination.pageSize, next);
  };

  const columns = [
    { title: "ID", dataIndex: "id", width: 70, align: "center" },
    {
      title: "Employee",
      key: "name",
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{r.full_name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {r.email || "—"}
          </Text>
        </Space>
      ),
    },
    { title: "Dept", dataIndex: "department", width: 120, ellipsis: true },
    { title: "Title", dataIndex: "job_title", width: 120, ellipsis: true },
    {
      title: "Linked user",
      key: "u",
      width: 140,
      render: (_, r) =>
        r.linked_user_name ? (
          <Tag>{r.linked_user_name}</Tag>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      width: 90,
      render: (a) => (
        <Tag color={a ? "success" : "default"}>{a ? "Active" : "Off"}</Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      render: (_, r) =>
        canWrite ? (
          <Space>
            <Tooltip title="Edit">
              <Button
                icon={<EditOutlined />}
                onClick={() => openEdit(r)}
                style={styles.ghostBtn}
              />
            </Tooltip>
            <Popconfirm title="Deactivate?" onConfirm={() => handleDelete(r)}>
              <Button danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
  ];

  return (
    <div>
      <Row
        align="middle"
        justify="space-between"
        style={{ marginBottom: 20, padding: "10px 10px" }}
      >
        <Col>
          <Space>
            <IdcardOutlined style={{ fontSize: 22, color: "black" }} />
            <Title level={4} style={{ margin: 0, color: "black" }}>
              Employee management
            </Title>
            <Tag color="blue">{pagination.total} total</Tag>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() =>
                fetchEmployees(
                  pagination.current,
                  pagination.pageSize,
                  searchText,
                )
              }
              style={styles.ghostBtn}
            />
            <Input.Search
              allowClear
              placeholder="Search name / email / dept"
              onSearch={handleSearch}
              onChange={(e) => {
                if (!e.target.value) handleSearch("");
              }}
              enterButton={<SearchOutlined />}
              style={{ width: 260 }}
            />
            {canWrite && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setCreateOpen(true)}
                style={styles.primaryBtn}
              >
                Add employee
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      <Card style={styles.tableCard} bodyStyle={{ padding: 0 }}>
        <Table
          columns={columns}
          dataSource={employees}
          rowKey="id"
          loading={tableLoading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
            onChange: (page, pageSize) =>
              fetchEmployees(page, pageSize, searchText),
            style: { padding: "16px 24px" },
          }}
          scroll={{ x: 720 }}
        />
      </Card>

      <Modal
        title="Add employee"
        open={createOpen}
        onCancel={() => {
          setCreateOpen(false);
          createForm.resetFields();
        }}
        footer={null}
        width={520}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreate}
          style={{ marginTop: 12 }}
        >
          <Form.Item name="employee_code" label="Employee code">
            <Input placeholder="Optional" />
          </Form.Item>
          <Form.Item
            name="full_name"
            label="Full name"
            rules={[{ required: true, message: "Required" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email">
            <Input type="email" />
          </Form.Item>
          <Form.Item name="phone" label="Phone">
            <Input />
          </Form.Item>
          <Form.Item name="department" label="Department">
            <Input />
          </Form.Item>
          <Form.Item name="job_title" label="Job title">
            <Input />
          </Form.Item>
          <Form.Item name="user_id" label="Link to app user">
            <Select
              allowClear
              placeholder="Optional"
              showSearch
              optionFilterProp="children"
            >
              {linkUsers.map((u) => (
                <Option key={u.id} value={u.id}>
                  {u.name} ({u.email})
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="hire_date" label="Hire date">
            <Input type="date" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={createLoading}>
                Create
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Edit employee"
        open={editOpen}
        onCancel={() => {
          setEditOpen(false);
          setSelected(null);
          editForm.resetFields();
        }}
        footer={null}
        width={520}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdate}
          style={{ marginTop: 12 }}
        >
          <Form.Item name="employee_code" label="Employee code">
            <Input />
          </Form.Item>
          <Form.Item
            name="full_name"
            label="Full name"
            rules={[{ required: true, message: "Required" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email">
            <Input type="email" />
          </Form.Item>
          <Form.Item name="phone" label="Phone">
            <Input />
          </Form.Item>
          <Form.Item name="department" label="Department">
            <Input />
          </Form.Item>
          <Form.Item name="job_title" label="Job title">
            <Input />
          </Form.Item>
          <Form.Item name="user_id" label="Link to app user">
            <Select allowClear showSearch optionFilterProp="children">
              {linkUsers.map((u) => (
                <Option key={u.id} value={u.id}>
                  {u.name} ({u.email})
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="hire_date" label="Hire date">
            <Input type="date" />
          </Form.Item>
          <Form.Item name="is_active" label="Status">
            <Select>
              <Option value={true}>Active</Option>
              <Option value={false}>Inactive</Option>
            </Select>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={editLoading}>
                Save
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

const styles = {
  tableCard: {
    borderRadius: 12,
    overflow: "hidden",
  },
  primaryBtn: {
    background: "rgb(120, 57, 120)",
    border: "none",
    borderRadius: 8,
  },
  ghostBtn: {
    background: "transparent",
    border: "1px solid #334155",
    borderRadius: 8,
  },
};

export default EmployeeManagement;
